/* eslint-disable unicorn/filename-case */
import { TrendUp01, XClose } from '@untitled-ui/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import { IconButton } from '../../../components/buttons/icon-button';
import { tGlobal, tNamespace } from '../../../services/translation';

export type KeywordSuggestion = {
  difficulty: number;
  primaryKeyword: string;
  volume: number;
};

const t = tNamespace('app.blogs.-keyword-suggestions-dropdown');

type KeywordSuggestionsDropdownProps = {
  isOpen: boolean;
  suggestions: KeywordSuggestion[];
  onSelect: (keyword: KeywordSuggestion) => void;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
};

type DifficultyLevel = 'na' | 'easy' | 'medium' | 'difficult';

function getDifficultyLevel(score: number): DifficultyLevel {
  if (score === 0) {
    return 'na';
  }
  if (score <= 29) {
    return 'easy';
  }
  if (score <= 59) {
    return 'medium';
  }
  return 'difficult';
}

function getDifficultyConfig(level: DifficultyLevel) {
  const config = {
    na: {
      label: '',
      dotColor: 'bg-gray-500',
    },
    easy: {
      label: t('easy'),
      dotColor: 'bg-green-500',
    },
    medium: {
      label: t('medium'),
      dotColor: 'bg-orange-400',
    },
    difficult: {
      label: t('difficult'),
      dotColor: 'bg-red-500',
    },
  };
  return config[level];
}

function formatSearchVolume(volume: number): string {
  if (volume >= 1000) {
    return `${(volume / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return `${volume}`;
}

function KeywordSuggestionItem(props: {
  suggestion: KeywordSuggestion;
  onSelect: (keyword: KeywordSuggestion) => void;
}) {
  const { suggestion, onSelect } = props;
  const { primaryKeyword, difficulty, volume } = suggestion;
  const difficultyLevel = getDifficultyLevel(difficulty);
  const difficultyConfig = getDifficultyConfig(difficultyLevel);

  return (
    <button
      type="button"
      className={twMerge(
        'grid w-full cursor-pointer grid-cols-5 items-center rounded-md px-3 py-4',
        'border border-transparent transition-colors hover:border-branding-accent-200 hover:bg-branding-accent-50'
      )}
      onClick={function () {
        onSelect(suggestion);
      }}
    >
      <span className="font-body-2-m col-span-3 overflow-hidden text-ellipsis whitespace-nowrap text-left text-black">
        {primaryKeyword}
      </span>
      <div className="col-span-1 flex items-center justify-start gap-1">
        <span className={twMerge('size-1.5 rounded-full', difficultyConfig.dotColor)} />
        <p className={twMerge('font-body-3-r text-dark-gray')}>
          {difficultyConfig.label} <span className="font-body-3-r text-gray">({difficulty})</span>
        </p>
      </div>
      {volume > 0 ? (
        <div className="col-span-1 flex items-center justify-end gap-1.5 text-branding-gray">
          <TrendUp01 width={16} height={16} />
          <p className="font-body-3-m text-dark-gray">
            {formatSearchVolume(volume)}
            <span className="font-body-3-r text-dark-gray">/mo</span>
          </p>
        </div>
      ) : (
        <div className="col-span-1 flex items-center justify-end gap-1.5 text-branding-gray">
          <TrendUp01 width={16} height={16} />
          <p className="font-body-3-m text-dark-gray">
            0-10
            <span className="font-body-3-r text-dark-gray">/mo</span>
          </p>
        </div>
      )}
    </button>
  );
}

export function KeywordSuggestionsDropdown(props: KeywordSuggestionsDropdownProps) {
  const { isOpen, suggestions, onSelect, onClose, triggerRef } = props;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(function () {
    // Find or create a portal container
    let container = document.querySelector('[data-radix-popper-content-wrapper]') as HTMLElement;
    if (container === null) {
      // If no Radix container, look for the dialog portal
      const dialogPortal = document.querySelector('[data-radix-portal]') as HTMLElement;
      container = dialogPortal !== null ? dialogPortal : document.body;
    }
    setPortalContainer(container);
  }, []);

  useEffect(
    function () {
      if (triggerRef == null) {
        return;
      }

      const triggerElement = triggerRef.current;
      if (!isOpen || triggerElement === null) {
        return;
      }

      function updatePosition() {
        if (triggerRef == null) {
          return;
        }

        const element = triggerRef.current;
        if (element === null) {
          return;
        }

        const triggerRect = element.getBoundingClientRect();
        setPosition({
          top: triggerRect.bottom + 12, // 12px gap (mt-3)
          left: triggerRect.left,
          width: triggerRect.width,
        });
      }

      updatePosition();

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return function () {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    },
    [isOpen, triggerRef]
  );

  if (!isOpen || suggestions.length === 0 || portalContainer === null) {
    return null;
  }

  const content = (
    <div
      ref={dropdownRef}
      className="fixed z-[9999]"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        pointerEvents: 'auto',
      }}
    >
      {/* Arrow pointer */}
      <div className="absolute -top-[8px] right-12 size-0 border-x-8 border-b-8 border-x-transparent border-b-branding-gray" />
      <div className="absolute -top-[6.5px] right-12 size-0 border-x-8 border-b-8 border-x-transparent border-b-white" />

      <div className="rounded-md border border-branding-gray bg-white shadow-2">
        <div className="flex items-center justify-between px-4 pb-[10px] pt-[14px]">
          <span className="font-body-2-r text-dark-gray">{t('keywordSuggestions')}</span>
          <IconButton
            icon={XClose}
            variant="outline"
            onClick={onClose}
            aria-label={tGlobal('close')}
            tabIndex={-1}
          />
        </div>

        <div className="flex max-h-[calc(100vh-200px)] min-h-[100px] flex-col gap-1 overflow-y-auto px-2 pb-2 md:max-h-[300px]">
          {suggestions.map(function (suggestion, index) {
            return (
              <KeywordSuggestionItem
                key={`${suggestion.primaryKeyword}-${index}`}
                suggestion={suggestion}
                onSelect={onSelect}
              />
            );
          })}
        </div>
      </div>
    </div>
  );

  return createPortal(content, portalContainer);
}
